import { makeStyles } from 'tss-react/mui';

/**
 * Parameters used to derive prop-dependent styles for the generic
 * `SidePanel` primitive.
 *
 * `tss-react` re-runs the style factory whenever any of them change,
 * so the rendered `<aside>` and scrim always reflect the latest drawer
 * state without a manual `sx` plumbing layer.
 */
type SidePanelStyleParams = {
  /** True while the panel is open; drives the slide transform + opacity. */
  panelOpen: boolean;
  /**
   * True when the configured `overlayBelowPx` media query matches.
   * Promotes the panel to overlay z-index so the scrim can sit just
   * below it.
   */
  isOverlay: boolean;
  /**
   * Offset (in pixels) applied to the panel + scrim top edge. Lets the
   * primitive render below a host app's top nav without knowing about
   * it. 0 = flush with viewport top.
   */
  topOffsetPx: number;
  /** Fixed panel width (px) at or above the `sm` breakpoint. */
  width: number;
};

/**
 * Styles for the generic right-side `SidePanel` and its optional scrim.
 *
 * Uses `tss-react/mui` `makeStyles` rather than
 * `@mui/material/styles` `styled()` because the latter resolves
 * `ThemeContext` to a host theme where `theme.new` can be undefined
 * inside Module-Federated bundles, causing a runtime `TypeError`.
 * `tss-react`'s `useTheme()` walks the React tree and always picks up
 * the `createBDTheme()` provider.
 */
const useStyles = makeStyles<SidePanelStyleParams>()(
  (theme, { panelOpen, isOverlay, topOffsetPx, width }) => ({
    panelSurface: {
      position: 'fixed',
      top: topOffsetPx,
      right: 0,
      bottom: 0,
      height: `calc(100dvh - ${topOffsetPx}px)`,
      width,
      backgroundColor: theme.new.color.common.white,
      borderLeft: `1px solid ${theme.new.color.line.hairline}`,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      zIndex: isOverlay ? theme.zIndex.modal : theme.zIndex.drawer,
      transform: panelOpen ? 'translateX(0)' : 'translateX(100%)',
      transition: theme.transitions.create('transform', {
        easing: panelOpen
          ? theme.transitions.easing.easeOut
          : theme.transitions.easing.sharp,
        duration: panelOpen
          ? theme.transitions.duration.enteringScreen
          : theme.transitions.duration.leavingScreen,
      }),
      pointerEvents: panelOpen ? 'auto' : 'none',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    scrim: {
      position: 'fixed',
      top: topOffsetPx,
      right: 0,
      bottom: 0,
      left: 0,
      width: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: theme.zIndex.modal - 1,
      opacity: panelOpen ? 1 : 0,
      pointerEvents: panelOpen ? 'auto' : 'none',
      transition: theme.transitions.create('opacity', {
        duration: panelOpen
          ? theme.transitions.duration.enteringScreen
          : theme.transitions.duration.leavingScreen,
      }),
      padding: 0,
      border: 0,
      cursor: 'pointer',
      appearance: 'none',
      font: 'inherit',
      color: 'inherit',
      '&:focus-visible': {
        outline: `2px solid ${theme.new.color.brand[600]}`,
        outlineOffset: -2,
      },
    },
    body: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      minHeight: 0,
      overflow: 'auto',
    },
  })
);

export default useStyles;
